import React from 'react'
import Home from 'pages/home'
import { trackedLazy } from 'lib/tracked-lazy'
import { Navigate, useLocation } from 'react-router-dom'

// trackedLazy = React.lazy that records its source path during SSR so
// the server can emit <link rel="modulepreload"> for the chunks the
// route actually used. The second argument MUST match a key in
// build/.vite/ssr-manifest.json — the source file the import resolves to.
const NewVenture = trackedLazy(() => import('pages/new-venture'), 'src/pages/new-venture/index.js')
const Careers = trackedLazy(() => import('pages/careers'), 'src/pages/careers/index.js')
// const ProductDesignLead = trackedLazy(
//   () => import('pages/careers/job-postings/productDesignLead'),
//   'src/pages/careers/job-postings/productDesignLead.jsx',
// )
const FeaturedAssets = trackedLazy(() => import('pages/featured-assets'), 'src/pages/featured-assets/index.ts')
const PressReleases = trackedLazy(() => import('pages/press-releases'), 'src/pages/press-releases/index.js')

/* ************************** LEGAL HUB ROUTES ************************** */
const LegalHub = trackedLazy(() => import('pages/legal-hub'), 'src/pages/legal-hub/index.ts')
const TermsOfUse = trackedLazy(
  () => import('pages/legal-hub/terms-of-use'),
  'src/pages/legal-hub/terms-of-use/index.ts',
)
const PrivacyNotice = trackedLazy(
  () => import('pages/legal-hub/privacy-notice'),
  'src/pages/legal-hub/privacy-notice/index.ts',
)
const CookiesPolicy = trackedLazy(
  () => import('pages/legal-hub/cookies-policy'),
  'src/pages/legal-hub/cookies-policy/index.ts',
)
const ConflictsOfInterest = trackedLazy(
  () => import('pages/legal-hub/conflicts-of-interest'),
  'src/pages/legal-hub/conflicts-of-interest/index.ts',
)
const ComplaintsHandlingPolicy = trackedLazy(
  () => import('pages/legal-hub/complaints-handling-policy'),
  'src/pages/legal-hub/complaints-handling-policy/index.ts',
)
const Whistleblowing = trackedLazy(
  () => import('pages/legal-hub/whistleblowing'),
  'src/pages/legal-hub/whistleblowing/index.ts',
)
const CustodyAndTransfer = trackedLazy(
  () => import('pages/legal-hub/custody-and-transfer'),
  'src/pages/legal-hub/custody-and-transfer/index.ts',
)
const EligibilityAndJurisdictions = trackedLazy(
  () => import('pages/legal-hub/eligibility-and-jurisdictions'),
  'src/pages/legal-hub/eligibility-and-jurisdictions/index.ts',
)

const StokePost = trackedLazy(() => import('pages/stoke-post'), 'src/pages/stoke-post/index.js')
const StokePostArticle = trackedLazy(
  () => import('pages/stoke-post/stoke-post-article'),
  'src/pages/stoke-post/stoke-post-article/index.js',
)
const Team = trackedLazy(() => import('pages/team/Team'), 'src/pages/team/Team.tsx')
const IntroductionToWalletsAndKeys = trackedLazy(
  () => import('pages/101/blockchain/introduction-to-wallets-and-keys'),
  'src/pages/101/blockchain/introduction-to-wallets-and-keys/index.js',
)
const TheDifferentWalletTypes = trackedLazy(
  () => import('pages/101/blockchain/the-different-wallet-types'),
  'src/pages/101/blockchain/the-different-wallet-types/index.js',
)
const WalletsSupportedBySTOKR = trackedLazy(
  () => import('pages/101/blockchain/wallets-supported-by-stokr'),
  'src/pages/101/blockchain/wallets-supported-by-stokr/index.js',
)
const GettingEther = trackedLazy(
  () => import('pages/101/blockchain/getting-ether'),
  'src/pages/101/blockchain/getting-ether/index.js',
)
const StayingSafe = trackedLazy(
  () => import('pages/101/blockchain/staying-safe'),
  'src/pages/101/blockchain/staying-safe/index.js',
)
const WhatIsAssetTokenisation = trackedLazy(
  () => import('pages/101/asset-tokenisation/WhatIsAssetTokenisation'),
  'src/pages/101/asset-tokenisation/WhatIsAssetTokenisation.jsx',
)
const TokenisationOverview = trackedLazy(
  () => import('pages/101/asset-tokenisation/WhyTokenization'),
  'src/pages/101/asset-tokenisation/WhyTokenization.jsx',
)
const InvestorWhitelisting = trackedLazy(
  () => import('pages/101/asset-tokenisation/InvestorWhitelisting'),
  'src/pages/101/asset-tokenisation/InvestorWhitelisting.jsx',
)
const PageNotFound = trackedLazy(() => import('pages/pageNotFound'), 'src/pages/pageNotFound/index.js')
const Newsletter = trackedLazy(() => import('pages/newsletter'), 'src/pages/newsletter/index.js')
const Products = trackedLazy(() => import('pages/products'), 'src/pages/products/Products.jsx')
const SpamReport = trackedLazy(() => import('pages/spam-report/SpamReport'), 'src/pages/spam-report/SpamReport.jsx')
const UnderConstruction = trackedLazy(() => import('pages/under-construction'), 'src/pages/under-construction/index.js')

/* Module Federation */
const InvestorDashboard = trackedLazy(() => import('investorDashboard/App'))
const VentureDashboard = trackedLazy(() => import('ventureDashboard/App'))
const InvestInSto = trackedLazy(() => import('investInSto/App'))
const SignUpFlow = trackedLazy(() => import('signUpFlow/App'))

interface RouterLink {
  to: string
  label: string
  children: React.ReactNode
  /** Federation remote — no SSR shell; show StokrLoader while remoteEntry loads. */
  isRemote?: boolean
}

const ManageUserHome = () => {
  const { search } = useLocation()
  return <Home isResetPassword={search.includes('resetPassword')} isVerifyEmail={search.includes('verifyEmail')} />
}

export const routes: RouterLink[] = [
  { to: '/404', label: '404', children: <PageNotFound /> },
  { to: '/page-not-found', label: 'Page Not Found', children: <PageNotFound /> },
  /* ************************** Module Federation Routes ************************** */
  { to: '/dashboard/*', label: 'Investor Dashboard', children: <InvestorDashboard />, isRemote: true },
  { to: '/admin/*', label: 'Venture Dashboard', children: <VentureDashboard />, isRemote: true },
  { to: '/invest/*', label: 'Invest in STO', children: <InvestInSto />, isRemote: true },
  { to: '/onboarding/*', label: 'Sign Up Flow', children: <SignUpFlow />, isRemote: true },
  /* ************************** LEGAL HUB ROUTES ************************** */
  { to: '/legal-hub', label: 'Legal Hub', children: <LegalHub /> },
  { to: '/legal-hub/stokr-terms-of-use', label: 'Terms of use', children: <TermsOfUse /> },
  { to: '/legal-hub/stokr-privacy-notice', label: 'Privacy Notice', children: <PrivacyNotice /> },
  { to: '/legal-hub/stokr-whistleblowing', label: 'Whistleblowing', children: <Whistleblowing /> },
  { to: '/legal-hub/stokr-cookies-policy', label: 'Cookies Policy', children: <CookiesPolicy /> },
  {
    to: '/legal-hub/stokr-complaints-handling-policy',
    label: 'Complaints Handling Policy',
    children: <ComplaintsHandlingPolicy />,
  },
  { to: '/legal-hub/stokr-conflicts-of-interest', label: 'Conflicts of Interest', children: <ConflictsOfInterest /> },
  {
    to: '/legal-hub/stokr-custody-and-transfer-of-crypto-assets',
    label: 'Custody and Transfer of Crypto-Assets',
    children: <CustodyAndTransfer />,
  },
  {
    to: '/legal-hub/stokr-eligibility-and-restricted-jurisdictions',
    label: 'Eligibility and Restricted Jurisdictions',
    children: <EligibilityAndJurisdictions />,
  },
  /* Legacy routes */
  { to: '/legal', label: 'Legal', children: <Navigate to="/legal-hub/stokr-terms-of-use" replace /> },
  { to: '/terms', label: 'Terms', children: <Navigate to="/legal-hub/stokr-terms-of-use" replace /> },
  { to: '/cookie-policy', label: 'Cookie Policy', children: <Navigate to="/legal-hub/stokr-cookies-policy" replace /> },
  {
    to: '/conflicts-of-interest',
    label: 'Conflicts of Interest',
    children: <Navigate to="/legal-hub/stokr-conflicts-of-interest" replace />,
  },
  { to: '/privacy', label: 'Privacy', children: <Navigate to="/legal-hub/stokr-privacy-notice" replace /> },
  {
    to: '/complaints',
    label: 'Complaints',
    children: <Navigate to="/legal-hub/stokr-complaints-handling-policy" replace />,
  },
  {
    to: '/whistleblowing',
    label: 'Whistleblowing',
    children: <Navigate to="/legal-hub/stokr-whistleblowing" replace />,
  },
  /* ************************** CAREERS & HIRING ROUTES ************************** */
  { to: '/careers', label: 'Careers', children: <Careers /> },
  // { to: '/careers/legal-officer', label: 'Legal Officer', children: <LegalOfficer /> },
  // { to: '/careers/product-design-lead', label: 'Head of Design', children: <ProductDesignLead /> },
  { to: '/hiring', label: 'Hiring', children: <Navigate to="/careers" replace /> },
  /* ************************** other PUBLIC ROUTES ************************** */
  { to: '/featured-assets', label: 'Featured Assets', children: <FeaturedAssets />, isRemote: true },
  { to: '/featured-assets/:venture', label: 'Featured Assets', children: <NewVenture /> },
  { to: '/manage-user', label: 'Manage User', children: <ManageUserHome /> },
  { to: '/newsletter', label: 'Newsletter', children: <Newsletter /> },
  { to: '/press-releases', label: 'Press Releases', children: <PressReleases /> },
  { to: '/press-releases/:url', label: 'Press Releases Article', children: <StokePostArticle /> },
  { to: '/solutions', label: 'Solutions', children: <Home /> },
  { to: '/solutions/:url', label: 'Solutions', children: <Products /> },
  { to: '/stoke-post/:url', label: 'Stoke Post Article', children: <StokePostArticle /> },
  { to: '/stoke-post', label: 'Stoke Post', children: <StokePost /> },
  { to: '/team', label: 'Team', children: <Team /> },
  { to: '/under-construction', label: 'Under Construction', children: <UnderConstruction /> },
  { to: '/spam-report', label: 'Spam Report', children: <SpamReport /> },
  /* ************************** 101 Routes ************************** */
  // { to: '/101', label: '101', children: <WhatIsAssetTokenisation/> },
  { to: '/101', label: '101', children: <Navigate to="/101/asset-tokenization/what-is-asset-tokenization" replace /> },
  { to: '/101/getting-on-the-blockchain/buying-crypto', label: 'Buying Crypto', children: <GettingEther /> },
  {
    to: '/101/getting-on-the-blockchain/introduction-to-wallets-and-keys',
    label: 'Introduction to Wallets and Keys',
    children: <IntroductionToWalletsAndKeys />,
  },
  {
    to: '/101/asset-tokenization/investor-whitelisting',
    label: 'Investor Whitelisting',
    children: <InvestorWhitelisting />,
  },
  { to: '/101/getting-on-the-blockchain/staying-safe', label: 'Staying Safe', children: <StayingSafe /> },
  {
    to: '/101/getting-on-the-blockchain/the-different-wallet-types',
    label: 'The Different Wallet Types',
    children: <TheDifferentWalletTypes />,
  },
  {
    to: '/101/asset-tokenization/tokenization-overview',
    label: 'Tokenisation Overview',
    children: <TokenisationOverview />,
  },
  {
    to: '/101/getting-on-the-blockchain/wallets-supported-by-stokr',
    label: 'Wallets Supported by STOKR',
    children: <WalletsSupportedBySTOKR />,
  },
  {
    to: '/101/asset-tokenization/what-is-asset-tokenization',
    label: 'What is Asset Tokenisation',
    children: <WhatIsAssetTokenisation />,
  },
  /* Catch-all last */
  { to: '*', label: 'All', children: <PageNotFound /> },
]
